import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { gsap, Power1 } from 'gsap';
import Lottie from 'lottie-react';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';
import useClickEnter from '@/shared/hooks/useClickEnter';
import useResponsive from '@/shared/hooks/useResponsive';
import { useLaneChooserTheme } from '@/features/laneChooserPhaser/theme';

import AudioToggleButton from '@/shared/components/audioToggle/AudioToggleButton';
import BackButton from '@/shared/components/button/BackButton';
import Button from '@/shared/components/button/Button';
import Tooltip, { TooltipColors, TooltipSizes } from '@/shared/components/tooltip/Tooltip';

import surfingSpeedwayIntro from '@/features/laneChooserPhaser/assets/sounds/surfing-speedway-intro.mp3';

import '@/features/laneChooserPhaser/styles/lane-chooser-start-screen.scss';

import type { CSSProperties } from 'react';

interface Props {
	onStart: () => void;
	onBack?: () => void;
}

const TOOLTIP_ANCHOR_ID = 'lane-chooser-start-screen-tooltip-anchor';

type LaneChooserStartScreenStyles = CSSProperties & {
	'--lane-chooser-start-screen-mobile'?: string;
	'--lane-chooser-start-screen-tablet'?: string;
	'--lane-chooser-start-screen-desktop'?: string;
};

const StartScreen = ({ onStart, onBack }: Props) => {
	const { t } = useTranslation();
	const { isMobilePortrait, isMobileLandscape, isTabletPortrait, isTabletLandscape, isPortrait, isLandscape } =
		useResponsive();
	const [showLottie, setShowLottie] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const hasStartedRef = useRef(false);
	const theme = useLaneChooserTheme();
	const mapUnfoldAnimation = theme.animations.startScreen.mapUnfold;
	const background = theme.animations.startScreen.background;
	const startScreenTranslationKey = `lane_chooser.start_screen.${theme.key}`;
	const startScreenTooltip = t(`${startScreenTranslationKey}.tooltip`);
	const startScreenButton = t(`${startScreenTranslationKey}.button`);
	const startScreenImages = theme.images.startScreen;
	const startScreenStyles: LaneChooserStartScreenStyles | undefined = startScreenImages
		? {
				'--lane-chooser-start-screen-mobile': `url("${startScreenImages.mobile}")`,
				'--lane-chooser-start-screen-tablet': `url("${startScreenImages.tablet}")`,
				'--lane-chooser-start-screen-desktop': `url("${startScreenImages.desktop}")`,
		  }
		: undefined;

	const { play: playIntroMusic, stop: stopIntroMusic } = useAudioPlayer({
		src: surfingSpeedwayIntro,
		loop: true,
		volume: 0.7,
	});

	const handleStart = () => {
		if (hasStartedRef.current) {
			return;
		}

		hasStartedRef.current = true;
		stopIntroMusic();
		const el = rootRef.current;
		if (el) {
			gsap.to(el, {
				opacity: 0,
				duration: 0.3,
				ease: Power1.easeOut,
				onComplete: () => onStart(),
			});
		} else {
			onStart();
		}
	};

	useClickEnter(handleStart);

	useEffect(() => {
		const timeoutId = setTimeout(() => setShowLottie(true), 1000);
		return () => clearTimeout(timeoutId);
	}, []);

	useEffect(() => {
		if (hasStartedRef.current) {
			stopIntroMusic();
			return () => {
				stopIntroMusic();
			};
		}
		playIntroMusic();
		return () => {
			stopIntroMusic();
		};
	}, [playIntroMusic, stopIntroMusic]);

	const animationData = useMemo(() => {
		if (!background) return null;
		if (isMobilePortrait) {
			return background.mobile;
		} else if (isMobileLandscape || isTabletLandscape) {
			return background.tabletLandscape;
		} else if (isTabletPortrait || isPortrait) {
			return background.tabletPortrait;
		} else {
			return background.desktop;
		}
	}, [isMobilePortrait, isMobileLandscape, isTabletLandscape, isTabletPortrait, isPortrait, background]);

	const preserveAspectRatio = isLandscape ? 'xMidYMid slice' : 'xMaxYMid slice';

	return (
		<div
			className={classNames(`lane-chooser-start-screen lane-chooser-start-screen--${theme.key}`)}
			style={startScreenStyles}
			role="dialog"
			aria-modal="true"
			aria-label={t('lane_chooser.start_screen.aria_label')}
			ref={rootRef}
		>
			{animationData && (
				<Lottie
					animationData={animationData}
					loop
					autoplay
					className="lane-chooser-start-screen__background"
					rendererSettings={{ preserveAspectRatio }}
				/>
			)}
			<div className="lane-chooser-start-screen__controls">
				<BackButton onClick={() => onBack?.()} />
				<AudioToggleButton tooltip={t('lane_chooser.audio_toggle.tooltip')} isOpen />
			</div>
			<div className="lane-chooser-start-screen__lottie" aria-hidden="true">
				{showLottie && mapUnfoldAnimation ? (
					<Lottie animationData={mapUnfoldAnimation} loop={false} autoplay />
				) : null}
			</div>
			<div
				id={TOOLTIP_ANCHOR_ID}
				className="lane-chooser-start-screen__tooltip-anchor"
				role="status"
				aria-live="polite"
			/>
			<Tooltip
				place="top"
				title={startScreenTooltip}
				anchor={TOOLTIP_ANCHOR_ID}
				color={TooltipColors.LIGHT}
				size={TooltipSizes.LARGE}
				isOpen
				className="lane-chooser-start-screen__tooltip"
			/>
			<div className="btn-group center lane-chooser-start-screen__button">
				<Button type="primary" iconName="icon-play" title={startScreenButton} onClick={handleStart} />
			</div>
		</div>
	);
};

export default StartScreen;
